import React, { useEffect, useRef, useState } from "react";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import TableCell from "@material-ui/core/TableCell";
import AddIcon from "@material-ui/icons/Add";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemText from "@material-ui/core/ListItemText/ListItemText";
import { useParams } from "react-router-dom";
import { Box, createStyles } from "@material-ui/core";
import CustomMenu from "../../../../../../components/Menu/CustomMenu";
import { tesaBlue, tesaGrey1, tesaGrey5 } from "../../../../../../styles/colors";
import TestRequestApi from "../../../../../../api/services/TestRequestApi";
import SubstrateSection from "../../../../../../api/models/test-series/SubstrateSection";
import useEnv from "../../../../../../hooks/env";
import TestSeriesApi from "../../../../../../api/services/TestSeriesApi";
import SubstrateActionValueGroup from "../../../../../../api/models/test-series/SubstrateActionValueGroup";
import RequestsPagePathParams from "../../../../../../api/models/test-request/RequestsPagePathParams";
import SubstrateSampleReferenceDTO from "../../../../../../api/models/substrate/SubstrateSampleReferenceDTO";

type SubstrateSample = {
  id: number;
  sampleName: string;
};

type SubstrateActionFlow = {
  substrateId: number;
  originTestCaseId?: number;
  actionFlow: { actionValues: []; name: string };
};

const useStyles = makeStyles((props: TestSeriesSubstrateHeaderProps) =>
  createStyles({
    root: {
      color: tesaGrey1,
      fontFamily: "Proxima Nova Bold",
      fontSize: "1rem",
      "border-right": props.substrateNumber ? "" : `1px solid ${tesaGrey5}`,
    },
    addIcon: {
      color: "#B1B2B3",
      float: "right",
    },
    columnHeadline: {
      color: "black",
    },
  }),
);

const StyledMenuItem = withStyles((theme) => ({
  root: {
    "&:focus": {
      backgroundColor: tesaBlue,
      "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem);

interface TestSeriesSubstrateHeaderProps {
  enabled: boolean;
  substrateNumber: string;
  cellSizeInPercent: number;
  handleAddSubstrate: (something: SubstrateActionValueGroup) => void;
  linkedTestSeriesParent: number | null;
  substrate: SubstrateSection;
  updateSubstrate: (substrate: SubstrateSection) => void;
}

const TestSeriesSubstrateHeader: React.FunctionComponent<TestSeriesSubstrateHeaderProps> = (props) => {
  const { api } = useEnv();
  const testRequestApi = new TestRequestApi(api);
  const testSeriesApi = new TestSeriesApi(api);
  const classes = useStyles(props);
  const {
    enabled,
    substrateNumber,
    cellSizeInPercent,
    handleAddSubstrate,
    updateSubstrate,
    substrate,
    linkedTestSeriesParent,
  } = props;
  const { testRequestId } = useParams<RequestsPagePathParams>();
  const menuRef = useRef({ close: () => {} });

  const [substrateSamples, setSubstrateSamples] = useState<SubstrateActionFlow[]>([]);

  const toSubstrateActionFlowObject = (substrateSample: SubstrateSample): SubstrateActionFlow => {
    return {
      substrateId: substrateSample.id,
      actionFlow: { actionValues: [], name: substrateSample.sampleName },
    };
  };

  const addSubstratesToTestSeries = (newSubstrateSamples: SubstrateSample[]) => {
    // if substrate Column does not come prefilled, fill it with the substrates from the test Request
    if (substrate?.substrateActionFlows?.length === 0) {
      const newSubstrateActionFlows = [] as any[];
      newSubstrateSamples.forEach((e: SubstrateSample) => {
        const substrateActionFlowObject = toSubstrateActionFlowObject(e);
        newSubstrateActionFlows.push(substrateActionFlowObject);
        substrate.substrateActionFlows = newSubstrateActionFlows;
        updateSubstrate({ ...substrate });
      });
    }
  };

  function convertToSubstrateObject(substrateSampleReferences?: SubstrateSampleReferenceDTO[]): SubstrateSample[] {
    const substrateArray = [] as any[];
    substrateSampleReferences?.forEach((e: any) => {
      substrateArray.push({
        id: e.substrateSampleId,
        sampleName: e.substrateSampleName,
      });
    });
    return substrateArray;
  }

  const getTestRequest = async () => {
    if (testRequestId !== "new") {
      if (linkedTestSeriesParent && substrate.originSubstrateSectionId) {
        testSeriesApi
          .fetchRegainedSubstrates(linkedTestSeriesParent, substrate.originSubstrateSectionId)
          .then((res) => {
            setSubstrateSamples(res.data);
          });
      } else {
        testRequestApi.getTestRequest(testRequestId).then((res) => {
          const substrateArray = convertToSubstrateObject(res.data.substrateSampleReferences);
          setSubstrateSamples(substrateArray.map(toSubstrateActionFlowObject));
          addSubstratesToTestSeries(substrateArray);
        });
      }
    }
  };

  useEffect(() => {
    getTestRequest();
  }, []);

  return (
    <TableCell width={`${cellSizeInPercent}%`} align="left" className={classes.root}>
      <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
        <b className={classes.columnHeadline}>
          Substrate {substrateNumber}{" "}
          {substrate.name.toLowerCase() !== "substrate" &&
            substrate.name.toLowerCase() !== "substrate 1" &&
            substrate.name.toLowerCase() !== "substrate 2" &&
            `(${substrate.name})`}
        </b>
        {enabled && (
          <CustomMenu alternativeButtonIcon={<AddIcon className={classes.addIcon} />} ref={menuRef}>
            {substrateSamples &&
              substrateSamples.map((substrateActionFlow: SubstrateActionFlow, index: number) => {
                return (
                  <StyledMenuItem
                    // eslint-disable-next-line react/no-array-index-key
                    key={index.toString(16)}
                    onClick={() => {
                      handleAddSubstrate({
                        substrateId: substrateActionFlow.substrateId,
                        originTestCaseId: substrateActionFlow.originTestCaseId,
                        actionFlow: {
                          name: substrateActionFlow.actionFlow.name,
                          actionValues: [],
                        },
                      });
                      menuRef.current.close();
                    }}
                  >
                    <ListItemText primary={substrateActionFlow.actionFlow.name} />
                  </StyledMenuItem>
                );
              })}
          </CustomMenu>
        )}
      </Box>
    </TableCell>
  );
};
export default TestSeriesSubstrateHeader;
